import React, { useEffect, useState } from 'react';
import { PageContainer, Text, SpaceEnd } from '../../../../styles/common.styles';
import {
    FilterContainer,
    NotificationContainer,
    CustomTopContainer,
    CustomBottomContainer,
    CustomTableSpace,
    NotificationsContentArea,
    NotificationsLoadingMask,
} from './ProjectNotifications.styles';
import { Select, Image, Space, Spin } from 'antd';
import { grayDark03 } from '../../../../styles/colors';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';

const { Option } = Select;
import { useModalState } from '../../../../hooks/modalState';
import {
    GetProjectInformationResponse,
    ProjectActivities,
    ProjectActivitiesResponse,
} from '../../../../redux/actions/projectActions/types';
import { actionMenu } from './ProjectNotifications.mockData';
import { ActionResponse } from '../../../../models/actionResponse/types';
import { getExternalProductsAction } from '../../../../redux/actions/transparencyCatalogActions';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../../redux/actions/transparencyCatalogActions/types';
import { ExternalProduct } from '../../../../models/product/types';
import CustomPagination from '../../../../components/common/pagination/CustomPagination';
import ErrorModal from '../../../../components/common/errorModal/ErrorModal';
import NotificationDateSection from './notificationDateSection/NotificationDateSection';
import { getUtcDateLabelForDayGroup } from '../../../../helpers/notificationDateHelper';
import { normalizeNotificationActivities } from '../../../../helpers/normalizeNotificationActivities';
import { NotificationSection } from '../../../../models/projectNotification/types';
import { getProjectActivitiesAction } from '../../../../redux/actions/projectActions';

interface Props {
    projectInfo: GetProjectInformationResponse | null;
    activeTab?: string;
}

const ProjectNotifications: React.FC<Props> = ({ projectInfo, activeTab }) => {
    const dispatch = useAppDispatch();
    const { projectReducer } = useAppSelector((state) => state);

    const [currentActivities, setCurrentActivities] = useState(Array<ProjectActivities>());
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedShowAction, setSelectedShowAction] = useState(actionMenu[0].key);
    const [pageSize] = useState(10);
    const [notificationSections, setNotificationSections] = useState<Array<NotificationSection>>(
        [],
    );
    const errorModal = useModalState();
    // Local flag only — do not use projectReducer.pending (dims the page and ties spinner to unrelated requests).
    const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);

    useEffect(() => {
        if (!projectInfo) {
            return;
        }
        // Refetch page 1 when products are added/copied/deleted elsewhere on the project (ProjectSlice bump).
        if (projectReducer.projectProductsChangedAt > 0) {
            setCurrentPage(1);
            getActivities(1, selectedShowAction);
            return;
        }
        getActivities();
    }, [projectInfo, pageSize, projectReducer.projectProductsChangedAt]);

    useEffect(() => {
        // Tab key "5" = Notifications — refresh when user opens this tab after changes on Products tab.
        if (activeTab === '5' && projectInfo) {
            setCurrentPage(1);
            getActivities(1, selectedShowAction);
        }
    }, [activeTab, projectInfo?.id]);

    useEffect(() => {
        const sections: NotificationSection[] = [];
        currentActivities.forEach((activity, i) => {
            sections.push({
                id: String(i + 1),
                name: getUtcDateLabelForDayGroup(activity),
                key: i,
                activities: activity,
                clicked: i === 0 ? true : false,
            });
        });
        setNotificationSections(sections);
    }, [currentActivities]);

    const getActivities = async (pageNumber: number = currentPage, selectedShow: string = selectedShowAction) => {
        if (!projectInfo) {
            return;
        }
        setIsNotificationsLoading(true);
        try {
            const action = await dispatch(
                getProjectActivitiesAction({
                    projectId: projectInfo.id,
                    curPage: pageNumber,
                    pageSize: pageSize,
                    showAction: selectedShow,
                }),
            );
            if (!getProjectActivitiesAction.fulfilled.match(action)) {
                errorModal.onOpen();
                return;
            }
            // API returns { data: NotificationInformation }; thunk payload may be wrapped or flat.
            const body = action.payload as ProjectActivitiesResponse & { data?: ProjectActivitiesResponse };
            const info = body?.data ?? body;
            const notifications = normalizeNotificationActivities(info?.notifications ?? []);
            await addProductsName(notifications);
            setCurrentActivities(notifications);
            const total = info?.totalPages ?? 1;
            setTotalPages(total);
            if (Math.ceil(total / pageSize) < pageNumber) {
                setPage(Math.ceil(total / pageSize), 'no');
            }
        } finally {
            setIsNotificationsLoading(false);
        }
    };

    const handleMenuSelect = async (selectedItem: string) => {
        setSelectedShowAction(selectedItem);
        setCurrentPage(1);
        // Always refetch from API; server applies showAction filter (no stale client-side filter).
        await getActivities(1, selectedItem);
    };

    const addProductsName = async (activities: Array<ProjectActivities>) => {
        const productIds = [
            ...new Set(
                activities
                    .map((a) => a.authors)
                    .reduce((acc, item) => [...acc, item], [])
                    .reduce((acc, item) => (acc as any).concat(item), [])
                    .map((a: any) => a.activities)
                    .flat()
                    .map((a: any) => a.projectProduct)
                    .map((a: any) => a?.productId),
            ),
        ].filter((id: number) => id);
        if (productIds && productIds.length) {
            const { payload } = (await dispatch(
                getExternalProductsAction({
                    productIds: productIds as Array<number>,
                }),
            )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;
            if (payload.data) {
                const products = Object.values(payload.data.mfSection).reduce(
                    (agg, item: any) => agg.concat(item),
                    Array<ExternalProduct>(),
                );
                activities.map((activity) => {
                    activity.authors = activity.authors.map((author) => {
                        author.activities = author.activities.map((authorActivity) => {
                            if (authorActivity.projectProduct) {
                                authorActivity.projectProduct.productName = products.find(
                                    (product) =>
                                        product.id === authorActivity.projectProduct.productId,
                                )?.productName;
                            }
                            return authorActivity;
                        });
                        return author;
                    });
                    return activity;
                });
            }
        }
    };

    const handleInstalledDivisionOpen = (id: string) => {
        const index = notificationSections.findIndex((division) => division.id === id);
        if (index !== -1) {
            const currentSections = [...notificationSections];
            currentSections[index].clicked = !currentSections[index].clicked;
            setNotificationSections(currentSections);
        }
    };

    const handleCurrentPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const currentCount = event.currentTarget.value;
        if (/^-?\d*\.?\d*$/.test(currentCount) || currentCount === '') {
            setCurrentPage(+event.currentTarget.value);
            if (+currentCount > Math.ceil(totalPages / pageSize)) {
                setTimeout(() => {
                    setPage(Math.ceil(totalPages / pageSize), "no");
                }, 1000);
            }
        }
        if (
            +event.currentTarget.value > 0 &&
            +event.currentTarget.value <= Math.ceil(totalPages / pageSize)
        ) {
            setPage(+event.currentTarget.value, "no");
        }
    };

    const setPage = (pageNumber: number, reloadActivities: string = "yes") => {
        setCurrentPage(pageNumber);
        if (reloadActivities === "yes") {
            getActivities(pageNumber);
        }
    };

    return (
        <>
            <PageContainer>

            <CustomTableSpace direction="horizontal" style={{marginBottom: 10 + `px`}}>
                <Space>
                    <FilterContainer>
                      <Text weight={400} height={16} size={13} color={grayDark03}>
                          Show
                      </Text>
                      <Select
                          aria-required
                          onChange={handleMenuSelect}
                          value={selectedShowAction}
                          suffixIcon={
                              <Image
                                  src="src/assets/images/ic-dropdown-arrow.svg"
                                     preview={false}
                              />
                          }
                       >
                           {actionMenu
                               ? actionMenu.map((item) => (
                                   <Option style={{fontSize: 12}} value={item.key} key={item.key}>
                                       {item.label}
                                   </Option>
                                 ))
                               : null}
                      </Select>
                      </FilterContainer>
                  </Space>
                  <SpaceEnd>
                      <CustomTopContainer direction="horizontal" style={{marginTop: 0 + `px`}}>
                          <CustomPagination
                              page={currentPage}
                              total={totalPages}
                              changePageNumber={setPage}
                              handleCurrentPage={handleCurrentPage}
                              pageSize={pageSize}
                          />
                      </CustomTopContainer>
                  </SpaceEnd>
            </CustomTableSpace>
                <NotificationsContentArea>
                    {isNotificationsLoading && (
                        <NotificationsLoadingMask aria-busy="true" aria-label="Loading notifications">
                            <Spin size="large" />
                        </NotificationsLoadingMask>
                    )}
                    <NotificationContainer>
                        {notificationSections.map((section, i) => (
                            <NotificationDateSection
                                notification={section}
                                key={i}
                                projectInfo={projectInfo}
                                handleClicked={handleInstalledDivisionOpen}
                                handleReload={getActivities}
                            />
                        ))}
                    </NotificationContainer>
                </NotificationsContentArea>
                <CustomBottomContainer direction="horizontal">
                    <CustomPagination
                        page={currentPage}
                        total={totalPages}
                        changePageNumber={setPage}
                        handleCurrentPage={handleCurrentPage}
                        pageSize={pageSize}
                    />
                </CustomBottomContainer>
            </PageContainer>
            {errorModal.isOpen && (
                <ErrorModal isOpen={errorModal.isOpen} closeModal={errorModal.onClose} />
            )}
        </>
    );
};

export default ProjectNotifications;
